import { Form, Formik, type FormikHelpers } from "formik";
import range from "lodash-es/range";
import { observable, makeObservable, computed, action } from "mobx";
import { inject, observer } from "mobx-react";
import { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { compose } from "nvent-web/utils/compose";
import * as yup from "yup";

import {
  AutocompleteField,
  type AutocompleteOption,
} from "nvent-web/components/form/AutocompleteField";
import { DropdownField } from "nvent-web/components/form/DropdownField";
import SubmitCancelButtons from "nvent-web/components/SubmitCancelButtons";
import { buildMessage } from "nvent-web/i18n/yup";
import { ProductsStore } from "nvent-web/stores/Products";
import { UserStore } from "nvent-web/stores/User";
import { type ProductSku } from "nvent-web/types/ProductSku";

import style from "./ByNumberForm.module.scss";

interface ByNumberFormProps {
  onSubmit: (productId: number, length?: number) => Promise<void>;
  onClose: () => void;
}

interface ByNumberFormInnerProps extends ByNumberFormProps {
  intl: IntlShape;
  products: ProductsStore;
  user: UserStore;
}

interface ProductOption
  extends
    AutocompleteOption,
    Pick<ProductSku, "pcn" | "ean" | "seNumber" | "noNumber"> {
  searchValue: string;
}

interface FormValues {
  productNumber: string;
  length?: number;
}

class ByNumberFormInner extends Component<ByNumberFormInnerProps> {
  private readonly initialValues: FormValues = { productNumber: "" };
  private readonly productsStore: ProductsStore;
  private readonly userStore: UserStore;

  constructor(props: ByNumberFormInnerProps) {
    super(props);

    this.productsStore = props.products;
    this.userStore = props.user;

    makeObservable<
      ByNumberFormInner,
      "productNumber" | "selectedProduct" | "schema"
    >(this, {
      productNumber: observable,
      selectedProduct: computed,
      schema: computed,
      setProductNumber: action.bound,
    });
  }

  setProductNumber(value: typeof this.productNumber) {
    this.productNumber = value;
  }

  private get productOptions(): ProductOption[] {
    const { countryCode } = this.userStore.companyAddress;
    return this.productsStore.availableProductSkus.map(
      ({ description, ean, pcn, seNumber = "", noNumber = "" }) => {
        let searchValue = ean;
        if (countryCode === "SE" && seNumber) {
          searchValue = seNumber;
        } else if (countryCode === "NO" && noNumber) {
          searchValue = noNumber;
        }
        return {
          key: ean,
          title: description,
          searchValue: normalizeNumber(searchValue),
          pcn: normalizeNumber(pcn),
          ean: normalizeNumber(ean),
          seNumber: normalizeNumber(seNumber),
          noNumber: normalizeNumber(noNumber),
        };
      }
    );
  }

  private productNumber = "";

  private get selectedProduct(): ProductSku | undefined {
    if (!this.productNumber) {
      return undefined;
    }

    return this.productsStore.availableProductSkus.find(
      ({ ean, pcn, seNumber = "", noNumber = "" }) =>
        normalizeNumber(ean) === this.productNumber ||
        normalizeNumber(pcn) === this.productNumber ||
        normalizeNumber(seNumber) === this.productNumber ||
        normalizeNumber(noNumber) === this.productNumber
    );
  }

  private get isLengthRequired(): boolean {
    return Boolean(this.selectedProduct && this.selectedProduct.customLength);
  }

  private readonly productLengthOptions = range(1, 101).map((value) => ({
    title: `${value} m`,
    key: value.toString(),
  }));

  private get schema() {
    const product = this.selectedProduct;

    const productNumber = yup
      .string()
      .test("product-found", buildMessage("error.notFound"), () => {
        return !!product;
      });

    let length = yup.string();

    if (this.isLengthRequired) {
      length = length.required();
    }

    return yup.object({ productNumber, length });
  }

  private isFormValid(values: FormValues): boolean {
    try {
      this.schema.validateSync(values, { abortEarly: false });
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return false;
    }
  }

  private get areEmptyCategoryOptions() {
    return this.productsStore.availableProductSkus.length === 0;
  }

  render() {
    const { onClose, intl } = this.props;
    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={this.schema}
        onSubmit={this.handleSubmit}
      >
        {({
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          values,
          validateForm,
        }) => (
          <Form noValidate={true} autoComplete="off">
            <p className={style.notice}>
              <FormattedMessage id="product.add.byNumber.description" />
            </p>

            <AutocompleteField
              disabled={this.areEmptyCategoryOptions}
              name="productNumber"
              label={<FormattedMessage id="product.add.byNumber" />}
              options={this.productOptions}
              filter={this.filterOption}
              onChange={(value) => {
                setFieldValue("productNumber", value);
                setFieldValue("length", "");
                setFieldTouched("length", false);
                this.onProductNumberChange(value);
                // TODO: Quick fix, issue with async schema validation?
                setTimeout(validateForm, 0);
              }}
              required
            />

            {this.areEmptyCategoryOptions && (
              <p className={style.warning}>
                <FormattedMessage id="product.add.byNumber.noProducts" />
              </p>
            )}

            {!!this.productNumber && this.selectedProduct && (
              <p>{this.selectedProduct.description}</p>
            )}

            {this.isLengthRequired && (
              <DropdownField
                name="length"
                label={<FormattedMessage id="form.productLength.label" />}
                options={this.productLengthOptions}
                placeholder={intl.formatMessage({
                  id: "form.productLength.selectPlaceholder",
                })}
                required
              />
            )}

            <div className={style.actions}>
              <SubmitCancelButtons
                onCancel={onClose}
                submitLabel={<FormattedMessage id="actions.add" />}
                disabled={!this.isFormValid(values)}
                loading={isSubmitting}
              />
            </div>
          </Form>
        )}
      </Formik>
    );
  }

  private onProductNumberChange = (productNumber: string) => {
    this.setProductNumber(normalizeNumber(productNumber));
  };

  private filterOption = (
    productNumber: string,
    { ean, pcn, seNumber = "", noNumber = "" }: ProductOption
  ) => {
    const normalizedNumber = normalizeNumber(productNumber);
    return (
      ean.includes(normalizedNumber) ||
      pcn.includes(normalizedNumber) ||
      seNumber.includes(normalizedNumber) ||
      noNumber.includes(normalizedNumber)
    );
  };

  private handleSubmit = async (
    { length }: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const { onSubmit } = this.props;

    try {
      if (this.selectedProduct) {
        await onSubmit(
          this.selectedProduct.id,
          this.isLengthRequired ? Number(length) : undefined
        );
      }
    } finally {
      setSubmitting(false);
    }
  };
}

const ByNumberForm = compose<ByNumberFormProps>(
  injectIntl,
  inject("products", "user"),
  observer
)(ByNumberFormInner);

export default ByNumberForm;

function normalizeNumber(productNumber: string) {
  return productNumber ? productNumber.toLowerCase().trim() : "";
}
